import React from 'react';
import { Info } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FORME_IMAGES, FORMES_EMBOUT, MATERIAL_LABELS, getShoresForMaterial, shoreLabel, getAvailableColors, colorLabel } from './orderFormConstants';

// Sélecteur de matière (Résine 3D / Silicone / Résine souple), piloté par MATERIAL_LABELS.
export function MatiereSelect({ value, onChange, disabled, exclude = [], testId = 'matiere-select', className = '' }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={`rounded-sm bg-white ${className}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">
        {Object.entries(MATERIAL_LABELS).filter(([k]) => !exclude.includes(k)).map(([k, lbl]) => (
          <SelectItem key={k} value={k} data-testid={`${testId}-${k}`}>{lbl}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Sélecteur de dureté : liste dépendante de la matière (silicone 25/35/40/60, résine souple 70/90).
export function DureteSelect({ matiere, value, onChange, shores, testId = 'durete-select', className = '' }) {
  const list = shores || getShoresForMaterial(matiere);
  if (!list.length) return null;
  // key : force le remontage quand la liste change (Radix SelectValue ne se rafraîchit pas sinon)
  return (
    <Select key={list.join('-')} value={value} onValueChange={onChange}>
      <SelectTrigger className={`rounded-sm bg-white ${className}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">
        {list.map((s) => <SelectItem key={s} value={s} data-testid={`${testId}-${s}`}>{shoreLabel(s)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// Sélecteur de couleur silicone : uniquement les couleurs disponibles pour TOUTES les duretés fournies.
// `only` / `exclude` permettent d'appliquer des règles métier locales (ex. obturateurs appairés).
export function CouleurSelect({ shores, value, onChange, only, exclude = [], testId = 'couleur-select', className = '' }) {
  let colors = getAvailableColors(shores).filter((c) => !exclude.includes(c));
  if (only) colors = colors.filter((c) => only.includes(c));
  return (
    <Select key={colors.join('-')} value={value} onValueChange={onChange}>
      <SelectTrigger className={`rounded-sm bg-white ${className}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">
        {colors.map((c) => (
          <SelectItem key={c} value={c} data-testid={`${testId}-${c}`}><ColorSwatch color={c} />{colorLabel(c)}</SelectItem>
        ))}
        {colors.length === 0 && <SelectItem value="transparent" disabled>Aucune couleur disponible pour cette dureté</SelectItem>}
      </SelectContent>
    </Select>
  );
}

export function OrangeInfoBox({ children }) {
  return (
    <div className="bg-orange-50 border border-orange-300 rounded-sm p-3 flex items-start gap-2.5 mt-2" data-testid="montage-info-box">
      <Info size={16} className="text-orange-600 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-orange-800">{children}</p>
    </div>
  );
}

// Pastille de couleur inline pour les dropdowns de sélection de couleur silicone.
// - transparent : damier gris pâle
// - rouge_bleu / bleu_rouge : demi-rouge / demi-bleu
// - couleurs unies : rouge, bleu, orange, noir, rose
const SWATCH_STYLE = {
  transparent: { background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 8px 8px' },
  rose: { background: '#fbcfe8' },
  fuchsia: { background: '#d946ef' },
  rouge: { background: '#dc2626' },
  bleu: { background: '#2563eb' },
  orange: { background: '#f97316' },
  noir: { background: '#111827' },
  rouge_bleu: { background: 'linear-gradient(90deg, #dc2626 50%, #2563eb 50%)' },
  bleu_rouge: { background: 'linear-gradient(90deg, #2563eb 50%, #dc2626 50%)' },
};

export function ColorSwatch({ color, className = '' }) {
  const style = SWATCH_STYLE[color];
  if (!style) return null;
  return (
    <span
      aria-hidden="true"
      data-testid={`color-swatch-${color}`}
      className={`inline-block w-3.5 h-3.5 rounded-full border border-zinc-300 mr-2 align-middle shrink-0 ${className}`}
      style={style}
    />
  );
}

export function FormeImage({ value }) {
  const src = FORME_IMAGES[value];
  if (!src) return null;
  const label = FORMES_EMBOUT.find(f => f.value === value)?.label || value;
  return (
    <div className="w-20 h-20 rounded-sm overflow-hidden border border-zinc-200 flex-shrink-0 bg-zinc-50" data-testid={`forme-img-${value}`}>
      <img src={src} alt={label} className="w-full h-full object-contain" />
    </div>
  );
}

// Photos associées aux options du formulaire (complétées au fil des visuels fournis)
const OPTION_IMAGES = {
  // Types d'embout auditif
  ecouteur_deporte: '/images/types/ric_ecouteur_deporte.jpg',
  bte: '/images/types/bte.jpg',
  integral: '/images/integral_phonak.png',
};

export function ImagePlaceholder({ label }) {
  const src = OPTION_IMAGES[label];
  if (!src) return null;
  return (
    <div className="w-20 h-20 rounded-sm overflow-hidden border border-zinc-200 flex-shrink-0 bg-white" data-testid={`option-img-${label}`}>
      <img src={src} alt={label} className="w-full h-full object-contain" />
    </div>
  );
}
